'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { useLocalStorageValue } from '@react-hookz/web';
import { CameraControls, Environment, GizmoHelper, GizmoViewport, Stage as StageWebGL } from '@react-three/drei';
import { Stage as StageWebGPU } from '@react-three/drei/webgpu';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import CameraControlsImpl from 'camera-controls';
import { CheckIcon, ChevronDownIcon, CopyIcon, ExpandIcon, FrameIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';

import type { RendererProps } from '@react-three/fiber';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

import { useSceneScreenshot } from '@/app/editor/hooks/useSceneScreenshot';
import { Button } from '@/components/ui/button';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { asyncLoadPrefabAsset } from '@/editor/core/actions/prefabActions';
import { AssetType, getAssetHandler, type IAssetFile } from '@/editor/core/assetHandlers/assetHandler';
import * as editorUtils from '@/editor/core/editorUtils';
import {
  type InspectorPreviewToneMappingChoice,
  PREVIEW_TONE_MAPPING_OPTIONS,
  resolvePreviewToneMapping,
} from '@/editor/core/preview/resolvePreviewToneMapping';
import { editorStore, observer, sceneStore } from '@/editor/store';
import { cn } from '@/lib/utils';

const DEBUG_LOG = {
  modelLoad: false,
  prefabLoad: false,
  materialLoad: false,
  cleanup: false
} as const;

const AssetDetailTitle = ({ title }: { title: string }) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className='flex items-center h-5 truncate font-normal text-xs'>{title}</div>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
};

const AssetDetailValue = ({ title, value }: { title: string, value: string | number }) => {
  const [copied, setCopied] = useState(false);
  const isCopyable = title !== 'Type';
  const displayValue = String(value);

  const handleCopy = async () => {
    if (!isCopyable) return;

    try {
      await navigator.clipboard.writeText(displayValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      console.warn('[InspectorPreview] Failed to copy asset detail value:', error);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex items-center h-5 gap-1 group',
            isCopyable && 'hover:cursor-pointer'
          )}
          onClick={handleCopy}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              void handleCopy();
            }
          }}
          role={isCopyable ? 'button' : undefined}
          tabIndex={isCopyable ? 0 : undefined}
        >
          <span
            className={cn(
              'truncate text-xs font-normal text-secondary-foreground transition-colors',
              isCopyable && 'group-hover:text-foreground'
            )}
          >
            {displayValue}
          </span>
          {isCopyable && (
            <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
              {copied ? (
                <CheckIcon className="size-3 text-primary-foreground" />
              ) : (
                <CopyIcon className="size-3" />
              )}
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>{value}</TooltipContent>
    </Tooltip>
  );
};

/**
 * PreviewPopupDialog - Large popup dialog for asset preview
 */
const PreviewPopupDialog = observer(({
  open,
  onOpenChange,
  asset,
  assetType,
  assetUrl,
  showPreviewScene,
  sceneScreenshotUrl
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: IAssetFile;
  assetType: AssetType;
  assetUrl: string;
  showPreviewScene: boolean;
  sceneScreenshotUrl: string | null;
}) => {
  const [isContentReady, setIsContentReady] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Track mounting state
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Reset content ready state when dialog opens/closes
  useEffect(() => {
    if (open && isMounted) {
      setIsContentReady(false);
      // Small delay to ensure DOM is ready, then mark content as ready
      const timer = setTimeout(() => setIsContentReady(true), 300);
      return () => clearTimeout(timer);
    } else {
      setIsContentReady(false);
    }
  }, [open, isMounted]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent
        className="max-w-none max-h-none p-0 gap-0 overflow-hidden flex flex-col"
        style={{
          width: '70vw',
          height: '90vh',
          maxWidth: '70vw',
          maxHeight: '90vh',
          margin: 0,
          padding: 0
        }}
        overlayExcludeTitleBar={true}
      >
        <DialogHeader className="px-6 py-3 border-b border-border shrink-0">
          <DialogTitle className="text-sm font-medium text-left">
            {asset.name} Preview
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex-1 relative overflow-hidden bg-background"
          style={{ margin: 0, padding: 0, width: '100%', height: '100%' }}
        >
          {isContentReady && isMounted ? (
            showPreviewScene ? (
              <div className="w-full h-full" style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}>
                <PreviewScene asset={asset} assetType={assetType} assetUrl={assetUrl} isExpanded={true} />
              </div>
            ) : assetType === AssetType.Scene ? (
              sceneScreenshotUrl ? (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <Image
                    src={sceneScreenshotUrl}
                    alt={asset.name}
                    className="max-h-full max-w-full object-contain"
                    width={1024}
                    height={1024}
                    unoptimized
                    style={{ width: 'auto', height: 'auto' }}
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-background">
                  <div className="text-muted-foreground text-sm">No preview available</div>
                </div>
              )
            ) : assetUrl ? (
              assetType === AssetType.Texture ? (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <Image
                    src={assetUrl}
                    alt="Asset Preview"
                    className="max-h-full max-w-full object-contain"
                    width={1024}
                    height={1024}
                    unoptimized
                    style={{ width: 'auto', height: 'auto' }}
                  />
                </div>
              ) : assetType === AssetType.HDRI ? (
                <div className="w-full h-full">
                  <PreviewHDRICanvas asset={asset} assetUrl={assetUrl} />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-background">
                  <div className="text-muted-foreground text-sm">Preview not available for this asset type</div>
                </div>
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-background">
                <div className="text-muted-foreground text-sm">No preview available</div>
              </div>
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background">
              {/* <div className="text-muted-foreground text-sm">Loading preview...</div> */}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

/**
 * InspectorPreview - inspector panel
 */
export const InspectorPreview = observer(({ asset, shouldShowAssetDetails = true }: { asset: IAssetFile; shouldShowAssetDetails?: boolean }) => {
  const { assetType, assetTypeName, url: assetUrl } = asset;
  const isSceneAsset = assetType === AssetType.Scene;
  const isPrefabAsset = assetType === AssetType.Prefab;
  const showPreviewScene = assetType === AssetType.Model || assetType === AssetType.Material || isPrefabAsset;
  const showPreview = showPreviewScene || assetType === AssetType.Texture || assetType === AssetType.HDRI || isSceneAsset;
  const showAssetDetailsStorage = useLocalStorageValue(`genesys-inspector-showAssetDetails-${assetTypeName}`, { defaultValue: true, initializeWithValue: false });
  const [showPopup, setShowPopup] = useState(false);
  const [textureDimensions, setTextureDimensions] = useState<{ width: number; height: number } | null>(null);
  const { url: sceneScreenshotUrl } = useSceneScreenshot(isSceneAsset ? asset.path : null, asset.thumbnailUpdateCounter);

  const canOpenAsset = useMemo(() => {
    switch (assetType) {
      case AssetType.Prefab:
      case AssetType.SourceCode:
      case AssetType.Json:
        return true;
      default:
        return false;
    }
  }, [assetType]);

  const openAsset = () => {
    const assetHandler = getAssetHandler(assetType);
    assetHandler.handleOpen(asset);
  };

  // Load texture dimensions for texture assets
  useEffect(() => {
    if (assetType === AssetType.Texture && assetUrl) {
      const img = document.createElement('img');
      img.onload = () => {
        setTextureDimensions({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        setTextureDimensions(null);
      };
      img.src = assetUrl;
    } else {
      setTextureDimensions(null);
    }
  }, [assetType, assetUrl]);

  // Asset details
  const { name, path, normalizedAbsolutePath, size, modifiedTime } = asset;

  return (
    <div className='flex flex-col w-full pb-1 my-1'>
      {/* Preview Area */}
      {showPreview && !showPopup && (
        <div className='flex justify-center bg-background p-0 relative'>
          {/* Expand to Popup */}
          <div className='absolute top-2 left-2 z-10'>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Toggle
                    variant="overlay"
                    size="sm"
                    pressed={showPopup}
                    onPressedChange={setShowPopup}
                  >
                    <ExpandIcon className='h-3 w-3' />
                  </Toggle>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Expanded Preview
              </TooltipContent>
            </Tooltip>
          </div>

          <div className='aspect-square w-full max-h-72 flex items-center justify-center'>
            {showPreviewScene ? <PreviewScene asset={asset} assetType={assetType} assetUrl={assetUrl} isExpanded={false} /> :
              isSceneAsset ? (
                sceneScreenshotUrl ? <Image src={sceneScreenshotUrl} alt={asset.name} className="max-h-full max-w-full object-contain" width={512} height={512} unoptimized style={{ width: 'auto', height: 'auto' }} /> : null
              ) : assetUrl ?
                assetType === AssetType.Texture ? <Image src={assetUrl} alt="Selected Asset" className="max-h-full max-w-full object-contain" width={512} height={512} unoptimized style={{ width: 'auto', height: 'auto' }} /> :
                  assetType === AssetType.HDRI ? <PreviewHDRICanvas asset={asset} assetUrl={assetUrl} /> :
                    null :
                null}
          </div>
        </div>
      )}
      {/* Asset Details Toggle */}
      {shouldShowAssetDetails && (
        <>
          <Button
            variant="clear"
            size="xs"
            onClick={() => showAssetDetailsStorage.set(!showAssetDetailsStorage.value)}
            className='flex items-center justify-between w-full px-4 border-b border-border bg-muted/20 h-5'
          >
            <span>Asset Details</span>
            <motion.div
              animate={{ rotate: showAssetDetailsStorage.value ? 180 : 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <ChevronDownIcon className='h-4 w-4' />
            </motion.div>
          </Button>
          {/* Asset Details */}
          <AnimatePresence initial={false}>
            {showAssetDetailsStorage.value && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
              >
                <ResizablePanelGroup orientation='horizontal' className='h-full w-full border-b border-border bg-muted/40 px-4 py-0.5'>
                  <ResizablePanel defaultSize={20} minSize={15} className='pr-2'>
                    <div className='flex flex-col gap-y-0'>
                      <AssetDetailTitle title='Type' />
                      <AssetDetailTitle title='Name' />
                      <AssetDetailTitle title='Path' />
                      <AssetDetailTitle title='Full Path' />
                      <AssetDetailTitle title='Modified' />
                      <AssetDetailTitle title='Size' />
                      {assetType === AssetType.Texture && <AssetDetailTitle title='Dimensions' />}
                    </div>
                  </ResizablePanel>
                  <ResizableHandle />
                  <ResizablePanel defaultSize={80} minSize={30} className='pl-2'>
                    <div className='flex flex-col gap-y-0'>
                      <AssetDetailValue title='Type' value={assetTypeName} />
                      <AssetDetailValue title='Name' value={name} />
                      <AssetDetailValue title='Path' value={path} />
                      <AssetDetailValue title='Full Path' value={normalizedAbsolutePath} />
                      <AssetDetailValue title='Modified' value={modifiedTime ? new Date(modifiedTime).toLocaleString() : '-'} />
                      <AssetDetailValue title='Size' value={size ? `${(size / 1024).toFixed(2)} KB` : '-'} />
                      {assetType === AssetType.Texture && (
                        <AssetDetailValue
                          title='Dimensions'
                          value={textureDimensions
                            ? `${textureDimensions.width}x${textureDimensions.height}${textureDimensions.width >= 2048 || textureDimensions.height >= 2048 ? ' ❗' : ''}`
                            : '-'
                          }
                        />
                      )}
                    </div>
                  </ResizablePanel>
                </ResizablePanelGroup>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
      {canOpenAsset && (
        <div>
          <Button variant="link" size="xs" className={'w-fit'} tabIndex={0}
            onClick={openAsset}>
            {`Open ${assetTypeName}`}
          </Button>
        </div>
      )}
      <div className='w-full h-px bg-border' />

      {/* Preview Popup Dialog */}
      <PreviewPopupDialog
        open={showPopup}
        onOpenChange={setShowPopup}
        asset={asset}
        assetType={assetType}
        assetUrl={assetUrl}
        showPreviewScene={showPreviewScene}
        sceneScreenshotUrl={sceneScreenshotUrl}
      />
    </div >
  );
});

// Available environment presets
const ENVIRONMENT_PRESETS = [
  'none', 'sunset', 'dawn', 'forest', 'city'
] as const;

type EnvironmentPreset = typeof ENVIRONMENT_PRESETS[number];

async function createInspectorPreviewRenderer(props: RendererProps): Promise<ENGINE.IGenesysRenderer> {
  const rendererProps = {
    canvas: props.canvas as HTMLCanvasElement,
    powerPreference: 'high-performance' as const,
    stencil: false,
    antialias: true,
    depth: true,
    alpha: true
  };

  try {
    const webgpuRenderer = await ENGINE.GenesysWebGPURenderer.create(rendererProps);
    return webgpuRenderer;
  } catch (error) {
    console.warn('[InspectorPreview] WebGPU initialization failed, falling back to WebGL renderer:', error);
    return ENGINE.GenesysWebGLRenderer.create({
      ...rendererProps,
      preserveDrawingBuffer: true
    });
  }
}

/** Applies resolved tonemap each frame so scene post-process changes stay in sync (not observable). */
function PreviewToneMappingSync() {
  const renderer = useThree(state => state.renderer);
  const lastMode = useRef<THREE.ToneMapping | null>(null);
  const lastExposure = useRef<number | null>(null);

  useFrame(() => {
    const choice = editorStore.inspectorPanel.previewToneMapping;
    const { mode, exposure } = resolvePreviewToneMapping(choice, sceneStore.world);
    if (lastMode.current !== mode || lastExposure.current !== exposure) {
      renderer.toneMapping = mode;
      renderer.toneMappingExposure = exposure;
      lastMode.current = mode;
      lastExposure.current = exposure;
    }
  });

  return null;
}

/**
 * PreviewScene - Preview of the asset in the inspector.
 */
const PreviewScene = observer(({ asset, assetType, assetUrl, isExpanded = false }: { asset: IAssetFile, assetType: AssetType, assetUrl: string, isExpanded?: boolean }) => {
  const [isWireframe, setIsWireframe] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [shouldResetCamera, setShouldResetCamera] = useState(false);
  const environmentPresetStorage = useLocalStorageValue('genesys-inspector-environmentPreset', {
    defaultValue: 'sunset' as EnvironmentPreset,
    initializeWithValue: false
  });

  // Track mounting state
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Validate and get environment preset, fallback to 'sunset' if invalid
  const environmentPreset = useMemo(() => {
    const storedValue = environmentPresetStorage.value;
    return ENVIRONMENT_PRESETS.includes(storedValue as EnvironmentPreset) ? storedValue : 'sunset';
  }, [environmentPresetStorage.value]);

  const setEnvironmentPreset = (value: EnvironmentPreset) => {
    environmentPresetStorage.set(value);
    // Trigger camera reset when environment changes
    setShouldResetCamera(true);
  };

  const handleResetCamera = () => {
    setShouldResetCamera(true);
  };

  const Stage = sceneStore.isWebGPU ? StageWebGPU : StageWebGL;

  return (
    <div className='w-full h-full flex pt-0 items-end relative' style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}>
      {/* Wireframe Toggle Button */}
      <div className='absolute top-2 right-2 z-10'>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Toggle
                variant="overlay"
                size="sm"
                pressed={isWireframe}
                onPressedChange={setIsWireframe}
              >
                <FrameIcon className='h-3 w-3' />
              </Toggle>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {isWireframe ? 'Disable Wireframe' : 'Enable Wireframe'}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Environment + tonemapping overlays */}
      <div className='absolute bottom-2 right-2 z-10 flex flex-row gap-2 items-center'>
        <Select value={environmentPreset} onValueChange={(value: EnvironmentPreset) => setEnvironmentPreset(value)}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <SelectTrigger size='xs' className='text-accent-foreground text-xs backdrop-blur-3xl capitalize'>
                  <SelectValue />
                </SelectTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Environment Preset
            </TooltipContent>
          </Tooltip>
          <SelectContent>
            {ENVIRONMENT_PRESETS.map(preset => (
              <SelectItem key={preset} value={preset} className={cn('text-xs capitalize text-accent-foreground')}>
                {preset === 'none' ? 'None' : preset}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={editorStore.inspectorPanel.previewToneMapping}
          onValueChange={(value: InspectorPreviewToneMappingChoice) => editorStore.inspectorPanel.setPreviewToneMapping(value)}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <SelectTrigger size='xs' className='text-accent-foreground text-xs backdrop-blur-3xl'>
                  <SelectValue />
                </SelectTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Tonemapping Preview
            </TooltipContent>
          </Tooltip>

          <SelectContent>
            {PREVIEW_TONE_MAPPING_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value} className='text-xs text-accent-foreground'>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isMounted && (
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="w-full h-full" style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}>
              <Canvas
                renderer={createInspectorPreviewRenderer}
                shadows
                camera={{ position: [0, 0, 4], near: 0.1, far: 1000 }}
                style={{ width: '100%', height: '100%', display: 'block', margin: 0, padding: 0 }}
                resize={{ scroll: false, debounce: 0 }}
              >
                <PreviewToneMappingSync />
                <Stage
                  intensity={1}
                  preset="rembrandt"
                  shadows={false}
                  adjustCamera={false}
                >
                  {environmentPreset !== 'none' && <Environment preset={environmentPreset} background blur={0.65} />}
                  <Suspense fallback={null}>
                    {assetUrl &&
                      assetType === AssetType.Model ? <ModelRender asset={asset} shouldResetCamera={shouldResetCamera} setShouldResetCamera={setShouldResetCamera} /> :
                      assetType === AssetType.Prefab ? <PrefabRender asset={asset} shouldResetCamera={shouldResetCamera} setShouldResetCamera={setShouldResetCamera} /> :
                        assetType === AssetType.Material ? <MaterialRender asset={asset} shouldResetCamera={shouldResetCamera} setShouldResetCamera={setShouldResetCamera} /> :
                          null
                    }
                  </Suspense>
                </Stage>
                <PreviewSceneCameraControls />
                <WireframeController isWireframe={isWireframe} />
                <InspectorGizmo isExpanded={isExpanded} />
              </Canvas>
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onClick={handleResetCamera}>
              Reset Camera
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      )}
    </div >
  );
});

const fallbackUnitBounds = new THREE.Box3().setFromCenterAndSize(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0.5, 0.5, 0.5));

/** Dynamic padding based on object size for better small object framing */
const getDynamicFitPadding = (bounds: THREE.Box3) => {
  const size = bounds.getSize(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z);

  // Use smaller padding for smaller objects, larger padding for bigger objects
  const paddingRatio = Math.max(0.05, Math.min(0.25, maxDimension * 0.15));

  return {
    paddingTop: paddingRatio,
    paddingBottom: paddingRatio,
    paddingLeft: paddingRatio,
    paddingRight: paddingRatio
  };
};

/**
 * ModelRender - Render the model in the preview scene.
 */
const ModelRender = observer(({ asset, shouldResetCamera, setShouldResetCamera }: {
  asset: IAssetFile;
  shouldResetCamera: boolean;
  setShouldResetCamera: (value: boolean) => void;
}) => {
  const [gltf, setGltf] = useState<GLTF | null>(null);
  const { scene } = useThree();

  // Handle camera reset when environment changes
  useEffect(() => {
    if (shouldResetCamera && gltf) {
      const controls = scene.userData.cameraControls as CameraControls;
      if (controls && controls.fitToBox && gltf.scene) {
        controls.reset();
        const bounds = editorUtils.getBounds(gltf.scene);
        const finalBounds = !bounds.isEmpty() ? bounds : fallbackUnitBounds;
        const padding = getDynamicFitPadding(finalBounds);
        controls.fitToBox(finalBounds, false, padding);
      }
      setShouldResetCamera(false);
    }
  }, [shouldResetCamera, gltf, scene, setShouldResetCamera]);

  useEffect(() => { // MODEL load
    let isCancelled = false;

    const loadModel = async () => {
      try {
        DEBUG_LOG.modelLoad && console.log(`🎭 Loading model: ${asset.path}`);
        const loadedGltf = await ENGINE.resourceManager.loadModel(ENGINE.AssetPath.fromString(asset.path));

        // Check if component was unmounted or asset changed during loading
        if (isCancelled) {
          DEBUG_LOG.modelLoad && console.log(`🚫 Model load cancelled for: ${asset.path}`);
          return;
        }

        if (loadedGltf) {
          DEBUG_LOG.modelLoad && console.log(`✅ Model loaded successfully: ${asset.path}`);
          setGltf(loadedGltf);

          // Access camera controls and fit to box with dynamic padding
          const controls = scene.userData.cameraControls as CameraControls;
          controls?.reset();
          if (controls && controls.fitToBox && loadedGltf.scene) {
            const bounds = editorUtils.getBounds(loadedGltf.scene);
            const finalBounds = !bounds.isEmpty() ? bounds : fallbackUnitBounds;
            const padding = getDynamicFitPadding(finalBounds);
            controls.fitToBox(finalBounds, false, padding);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.warn(`❌ Error loading model ${asset.path}:`, error);
        }
      }
    };

    // Clear previous model immediately when asset changes
    setGltf(null);
    loadModel();

    // Cleanup function to cancel pending requests
    return () => {
      isCancelled = true;
      DEBUG_LOG.cleanup && console.log(`🧹 Cleanup model load for: ${asset.path}`);
    };
  }, [asset.path, asset.previewUpdateCounter, scene]);

  return gltf ? <primitive castShadow receiveShadow object={gltf.scene} /> : null;
});

/**
 * PrefabRender - Render the prefab in the preview scene.
 */
const PrefabRender = observer(({ asset, shouldResetCamera, setShouldResetCamera }: {
  asset: IAssetFile;
  shouldResetCamera: boolean;
  setShouldResetCamera: (value: boolean) => void;
}) => {
  const { scene } = useThree();
  const currentModelRef = useRef<THREE.Object3D | null>(null);
  const [sceneActor, setSceneActor] = useState<ENGINE.Actor | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle camera reset when environment changes
  useEffect(() => {
    if (shouldResetCamera && sceneActor && !isLoading) {
      const controls = scene.userData.cameraControls as CameraControls;
      if (controls && controls.fitToBox) {
        controls.reset();
        const bounds = editorUtils.getActorBounds(sceneActor);
        const finalBounds = !bounds.isEmpty() ? bounds : fallbackUnitBounds;
        const padding = getDynamicFitPadding(finalBounds);
        controls.fitToBox(finalBounds, false, padding);
      }
      setShouldResetCamera(false);
    }
  }, [shouldResetCamera, sceneActor, isLoading, scene, setShouldResetCamera]);

  useEffect(() => {
    let isCancelled = false;

    const loadPrefab = async () => {
      try {
        setIsLoading(true);
        DEBUG_LOG.prefabLoad && console.log(`🧩 Loading prefab: ${asset.path}`);

        const actor = await asyncLoadPrefabAsset(asset.path);

        // Check if component was unmounted or asset changed during loading
        if (isCancelled) {
          DEBUG_LOG.prefabLoad && console.log(`🚫 Prefab load cancelled for: ${asset.path}`);
          return;
        }

        DEBUG_LOG.prefabLoad && console.log(`✅ Prefab loaded successfully: ${asset.path}`);
        setSceneActor(actor);
        setShouldResetCamera(true);
      } catch (error) {
        if (!isCancelled) {
          console.warn(`❌ Error loading prefab ${asset.path}:`, error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    // Clear previous actor immediately when asset changes
    setSceneActor(null);
    currentModelRef.current = null;
    loadPrefab();

    // Cleanup function to cancel pending requests
    return () => {
      isCancelled = true;
      DEBUG_LOG.cleanup && console.log(`🧹 Cleanup prefab load for: ${asset.path}`);
      setIsLoading(false);
    };
  }, [asset.path, asset.previewUpdateCounter, setShouldResetCamera]);

  useFrame(
    (_state, _delta) => {
      if (sceneActor && !isLoading) {
        const gltfMeshComponent = sceneActor.getComponent(ENGINE.ModelMeshComponent);
        if (gltfMeshComponent) {
          const model = gltfMeshComponent.getModel();

          // Check if model has changed
          if (model !== currentModelRef.current) {
            currentModelRef.current = model;

            // Apply fit-to-box logic if model is valid with dynamic padding
            if (model) {
              const controls = scene.userData.cameraControls as CameraControls;
              controls?.reset();
              if (controls && controls.fitToBox) {
                const bounds = editorUtils.getActorBounds(sceneActor);
                const finalBounds = !bounds.isEmpty() ? bounds : fallbackUnitBounds;
                const padding = getDynamicFitPadding(finalBounds);
                controls.fitToBox(finalBounds, false, padding);
              }
            }
          }
        }
      }
    },
    { phase: 'update' },
  );

  return sceneActor ? <primitive key={asset.path} object={sceneActor?.rootComponent} /> : null;
});

/**
 * MaterialRender - Render the material in the preview scene.
 */
const MaterialRender = observer(({ asset, shouldResetCamera, setShouldResetCamera }: {
  asset: IAssetFile;
  shouldResetCamera: boolean;
  setShouldResetCamera: (value: boolean) => void;
}) => {
  const [material, setMaterial] = useState<THREE.Material | null>(null);
  const { scene } = useThree();

  // Handle camera reset when environment changes
  useEffect(() => {
    if (shouldResetCamera && material) {
      const controls = scene.userData.cameraControls as CameraControls;
      if (controls && controls.fitToBox) {
        controls.reset();
        // For materials, use a smaller sphere bounds to fill more of the preview
        const bounds = new THREE.Box3().setFromCenterAndSize(new THREE.Vector3(0, 0, 0), new THREE.Vector3(1.5, 1.5, 1.5));
        controls.fitToBox(bounds, false, { paddingTop: 0.08, paddingBottom: 0.08, paddingLeft: 0.08, paddingRight: 0.08 });
      }
      setShouldResetCamera(false);
    }
  }, [shouldResetCamera, material, scene, setShouldResetCamera]);

  useEffect(() => { // MATERIAL load
    let isCancelled = false;

    const loadMaterial = async () => {
      try {
        DEBUG_LOG.materialLoad && console.log(`🎨 Loading material: ${asset.path}`);
        const loadedMaterial = await ENGINE.resourceManager.loadMaterial(ENGINE.AssetPath.fromString(asset.path));

        // Check if component was unmounted or asset changed during loading
        if (isCancelled) {
          DEBUG_LOG.materialLoad && console.log(`🚫 Material load cancelled for: ${asset.path}`);
          return;
        }

        if (loadedMaterial) {
          DEBUG_LOG.materialLoad && console.log(`✅ Material loaded successfully: ${asset.path}`);
          setMaterial(loadedMaterial);

          // For materials, fit to a smaller sphere to fill more of the preview
          const controls = scene.userData.cameraControls as CameraControls;
          controls?.reset();
          if (controls && controls.fitToBox) {
            const bounds = new THREE.Box3().setFromCenterAndSize(new THREE.Vector3(0, 0, 0), new THREE.Vector3(1.5, 1.5, 1.5));
            controls.fitToBox(bounds, false, { paddingTop: 0.08, paddingBottom: 0.08, paddingLeft: 0.08, paddingRight: 0.08 });
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.warn(`❌ Error loading material ${asset.path}:`, error);
        }
      }
    };

    loadMaterial();

    // Cleanup function to cancel pending requests
    return () => {
      isCancelled = true;
      DEBUG_LOG.cleanup && console.log(`🧹 Cleanup material load for: ${asset.path}`);
    };
  }, [asset.path, asset.previewUpdateCounter, scene]);

  return material ? (
    <mesh material={material} rotation={[THREE.MathUtils.degToRad(15), 0, 0]} >
      {/* <boxGeometry args={[1, 1, 1]} /> */}
      <sphereGeometry args={[1, 32, 32]} />
    </mesh >
  ) : null;
});

/**
 * PreviewSceneCameraControls - Camera controls for the preview scene.
 */
const PreviewSceneCameraControls = observer(() => {
  const { scene } = useThree();
  const cameraControlsRef = useRef<CameraControls>(null);

  // Store the controls reference in the scene userData so other components can access it
  useEffect(() => {
    if (cameraControlsRef.current) {
      scene.userData.cameraControls = cameraControlsRef.current;
    }
  }, [scene]);

  return (
    <CameraControls
      ref={cameraControlsRef}
      makeDefault
      mouseButtons={{
        left: CameraControlsImpl.ACTION.ROTATE, // Left mouse button for rotate
        middle: CameraControlsImpl.ACTION.TRUCK, // Middle mouse button for pan
        right: CameraControlsImpl.ACTION.NONE, // Disable right mouse button to allow context menu
        wheel: CameraControlsImpl.ACTION.DOLLY // Mouse wheel for zoom
      }}
    />
  );
});

/**
 * WireframeController - Controls wireframe mode by setting scene's overrideMaterial
 */
const WireframeController = ({ isWireframe }: { isWireframe: boolean }) => {
  const { scene } = useThree();

  useEffect(() => {
    // Toggle wireframe mode
    if (isWireframe) {
      scene.overrideMaterial = editorStore.wireframeMaterial;
    } else {
      scene.overrideMaterial = null;
    }

    // Cleanup on unmount
    return () => {
      scene.overrideMaterial = null;
    };
  }, [isWireframe, scene]);

  return null;
};

/**
 * PreviewHDRIPlane - Render the HDRI plane in the preview scene.
 */
const PreviewHDRIPlane = ({ asset, assetUrl }: { asset: IAssetFile, assetUrl: string }) => {
  const texture = useLoader(HDRLoader, assetUrl);
  texture.mapping = THREE.EquirectangularReflectionMapping;//
  return <mesh>
    <planeGeometry args={[2, 2]} />
    <meshBasicMaterial map={texture} />
  </mesh>;
};

/**
 * PreviewHDRICanvas - Render the HDRI canvas in the preview scene.
 */
const PreviewHDRICanvas = ({ asset, assetUrl }: { asset: IAssetFile, assetUrl: string }) => {
  const [isMounted, setIsMounted] = useState(false);

  // Track mounting state
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  return (
    <div className='w-full h-full' style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}>
      {isMounted && (
        <Canvas
          renderer
          camera={{ zoom: 1, position: [0, 0, 1], near: 0.1, far: 1000 }}
          style={{ width: '100%', height: '100%', display: 'block', margin: 0, padding: 0 }}
          resize={{ scroll: false, debounce: 0 }}
        >
          <PreviewHDRIPlane asset={asset} assetUrl={assetUrl} />
        </Canvas>
      )}
    </div>
  );
};

/**
 * InspectorGizmo - Gizmo helper for the inspector preview
 */
const InspectorGizmo = observer(({ isExpanded }: { isExpanded: boolean }) => {
  const { invalidate } = useThree();

  return (
    <GizmoHelper
      alignment="bottom-left"
      margin={isExpanded ? [80, 80] : [35, 35]}
      onUpdate={() => invalidate()}
      renderPriority={1}
    >
      <GizmoViewport
        axisColors={['#FC3652', '#4CAF50', '#4176F6']}
        labelColor="white"
        font="20px Inter, sans-serif"
        axisHeadScale={1}
        labels={['X', 'Y', 'Z']}
        hideNegativeAxes={false}
        hideAxisHeads={!isExpanded}
      />
    </GizmoHelper>
  );
});

InspectorPreview.displayName = 'InspectorPreview';
PreviewPopupDialog.displayName = 'PreviewPopupDialog';
AssetDetailTitle.displayName = 'AssetDetailTitle';
AssetDetailValue.displayName = 'AssetDetailValue';
PreviewScene.displayName = 'PreviewScene';
ModelRender.displayName = 'ModelRender';
PrefabRender.displayName = 'PrefabRender';
MaterialRender.displayName = 'MaterialRender';
PreviewSceneCameraControls.displayName = 'PreviewSceneCameraControls';
WireframeController.displayName = 'WireframeController';
PreviewHDRIPlane.displayName = 'PreviewHDRIPlane';
InspectorGizmo.displayName = 'InspectorGizmo';